import { parseWithZod } from "@conform-to/zod";
import { type ActionFunctionArgs, json, redirect } from "@remix-run/node";
import { useFetcher, useNavigate } from "@remix-run/react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Button } from "#app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "#app/components/ui/dialog";
import { Icon } from "#app/components/ui/icon";
import { requireUserId } from "#app/utils/auth.server";
import { useDebounce } from "#app/utils/misc.tsx";
import parseUrlMetadata from "#app/utils/parse-url-metadata";
import {
  addToUserContentLibrary,
  getOrCreateArticle,
  isInContentLibrary,
} from "../../../prisma/utils/media";

const AddArticleSchema = z.object({
  url: z.string(),
});

export async function action({ request }: ActionFunctionArgs) {
  const userId = await requireUserId(request);
  const formData = await request.formData();
  const submission = parseWithZod(formData, {
    schema: AddArticleSchema,
  });
  if (submission.status !== "success") {
    throw new Error("Invalid submission; this should never happen.");
  }
  const { url } = submission.value;
  const metadata = await parseUrlMetadata(url, { requireFullData: false });
  if (!metadata) {
    return json({ failureReason: "Error parsing URL." });
  }

  const { canonicalUrl, title, thumbnail } = metadata;
  const article = await getOrCreateArticle(canonicalUrl, title, thumbnail);
  if (await isInContentLibrary(article.mediaId, userId)) {
    return json({ failureReason: "This article is already in your library." });
  }
  await addToUserContentLibrary(article.mediaId, userId);
  return redirect(`/article/${article.mediaId}`);
}

export default function AddArticleRoute() {
  const [url, setUrl] = useState<string>("");
  const [isCheckingUrl, setIsCheckingUrl] = useState<boolean>(false);
  const [isInvalidUrl, setIsInvalidUrl] = useState<boolean>(false);
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(
    null
  );
  const navigate = useNavigate();
  const fetcher = useFetcher<typeof action>();

  const handleNewUrl = useDebounce(async () => {
    try {
      new URL(url); // This will throw if the URL is invalid
      fetcher.submit({ url }, { method: "POST" });
    } catch (error) {
      setIsInvalidUrl(true);
      setIsCheckingUrl(false);
    }
  }, 2000);

  // If we get any response from the server here, it means there was an error because otherwise
  // we would have redirected to the article page.

  useEffect(() => {
    const serverResponseReason = fetcher.data?.failureReason;
    if (serverResponseReason) {
      setServerErrorMessage(serverResponseReason);
      setIsCheckingUrl(false);
    }
  }, [fetcher.data]);

  return (
    <Dialog open={true} onOpenChange={() => navigate(-1)}>
      <DialogContent className="h-fit min-w-[60%]">
        <DialogHeader>
          <DialogTitle className="pb-1">
            <Icon name="reader" size="lg" /> Add an article
          </DialogTitle>
        </DialogHeader>
        <div className="h-fit rounded-lg border">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <input
              type="text"
              placeholder="Enter a URL..."
              value={url}
              onChange={(e) => {
                const newUrl = e.target.value;
                setUrl(newUrl);
                setIsInvalidUrl(false);
                setServerErrorMessage(null);
                if (newUrl.length > 0) {
                  setIsCheckingUrl(true);
                  handleNewUrl();
                }
              }}
              className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
            />
            {isCheckingUrl && (
              <Icon
                size="xs"
                name="update"
                className="ml-2 animate-spin"
                title="loading"
              />
            )}
          </div>
        </div>
        {isInvalidUrl && (
          <span className="ml-1 text-xs text-red-500">Not a valid URL.</span>
        )}
        {serverErrorMessage && (
          <span className="ml-1 text-xs text-red-500">
            {serverErrorMessage}
          </span>
        )}
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              navigate(-1);
            }}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
